/**
 * Statut du run racine ETL — source unique pour SyncStatusPanel + SyncManagerPage.
 * Polling adaptatif (G_POLL_03) : 4 s pendant un run ou en attente de confirmation,
 * 30 s au repos pour limiter les silent-renew MSAL.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@gs/auth';
import { ApiUnauthorizedError } from '@gs/api-client';
import { api } from '../services/api';

export interface SyncLog {
  id: string;
  app_name: string;
  app_key: string;
  source_key?: string | null;
  direction: string;
  status: 'running' | 'ok' | 'partial' | 'error' | 'skipped';
  started_at: string;
  ended_at: string | null;
  records: number | null;
  duration_ms: number | null;
  message: string | null;
}

export interface Estimate {
  durationMs: number;
  basedOnCount: number;
  groupedBy: 'source_key' | 'app_id+direction';
}

export type EstimateReason = 'insufficient_data' | 'too_dispersed';

export interface SyncRunningStatusResponse {
  log: SyncLog | null;
  estimate: Estimate | null;
  estimateReason: EstimateReason | null;
}

export const SYNC_RUNNING_QUERY_KEY = ['sync-status-running'] as const;

const POLL_ACTIVE_MS = 4000;
const POLL_IDLE_MS = 30000;

export function runningSourceKeyFromLog(log: SyncLog | null | undefined): string | null {
  if (log?.status !== 'running') return null;
  return log.source_key ?? log.app_key ?? null;
}

/**
 * @param activePoll — true entre le 202 et la première confirmation `running`
 *   (ou tant qu'un run tourne). SyncManagerPage passe `!!pendingSourceKey`.
 */
export function useSyncRunningStatus(activePoll = false) {
  const { user } = useAuth();

  return useQuery({
    queryKey: SYNC_RUNNING_QUERY_KEY,
    queryFn: () => api.get<SyncRunningStatusResponse>('/api/syncs/status/running'),
    refetchInterval: (query) => {
      if (query.state.error instanceof ApiUnauthorizedError) return false;
      const status = query.state.data?.log?.status;
      if (status === 'running' || activePoll || status === undefined) return POLL_ACTIVE_MS;
      return POLL_IDLE_MS;
    },
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    enabled: !!user,
  });
}
